import React from "react";
import { useNavigate } from "react-router-dom";
import { B2BLayout } from "../../components/b2b/B2BLayout";
import { IMG, useB2BSettings } from "../../lib/b2b";
import { ArrowRight, CalendarCheck, Camera, UserCog, AlertTriangle, Clock, Search, ClipboardList, Wrench, Building2, MapPin, ChevronRight } from "lucide-react";

const Problem = ({ icon: Icon, title, text }) => (
  <div className="rounded-2xl border border-[#0B1F3A]/10 bg-white p-5">
    <Icon className="h-6 w-6 text-[#F97316]" />
    <h3 className="mt-3 font-display text-base font-bold text-[#0B1F3A]">{title}</h3>
    <p className="mt-1 text-sm text-[#0B1F3A]/60">{text}</p>
  </div>
);
const Step = ({ n, title, text }) => (
  <div className="relative rounded-2xl bg-[#0B1F3A] p-5 text-white">
    <span className="font-display text-3xl font-extrabold text-[#0E7C7B]">{n}</span>
    <h3 className="mt-1 font-display text-base font-bold">{title}</h3>
    <p className="mt-1 text-sm text-white/60">{text}</p>
  </div>
);

export default function B2BHome() {
  const nav = useNavigate();
  const s = useB2BSettings();
  const hero = s?.hero || {};
  return (
    <B2BLayout>
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-14 lg:grid-cols-2 lg:py-20">
        <div>
          <span className="inline-block rounded-full bg-[#0E7C7B]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-[#0E7C7B]">Manutenzione programmata · Roma</span>
          <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight tracking-tight text-[#0B1F3A] sm:text-5xl">
            {hero.title || "La manutenzione dei tuoi alloggi, organizzata prima dei guasti."}
          </h1>
          <p className="mt-4 text-base text-[#0B1F3A]/70">
            {hero.subtitle || "Controlli programmati, report fotografici e un referente per gestire le anomalie. Per host, property manager e strutture ricettive a Roma."}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button data-testid="home-cta-primary" onClick={() => nav("/contatti")} className="flex items-center gap-2 rounded-full bg-[#F97316] px-6 py-3.5 font-bold text-white shadow-lg shadow-[#F97316]/25 transition hover:brightness-110 active:scale-[0.98]">Richiedi una proposta <ArrowRight className="h-5 w-5" /></button>
            <button data-testid="home-cta-secondary" onClick={() => nav("/piani")} className="rounded-full border border-[#0B1F3A]/15 bg-white px-6 py-3.5 font-bold text-[#0B1F3A] transition hover:border-[#0E7C7B]">Confronta i piani</button>
          </div>
        </div>
        <div className="relative">
          <img src={IMG.hero} alt="Appartamento curato e luminoso" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl" />
          <div className="absolute -bottom-5 -left-5 hidden rounded-2xl bg-[#0E7C7B] p-4 text-white shadow-xl sm:block">
            <CalendarCheck className="h-6 w-6" />
            <p className="mt-1 text-sm font-bold">Controlli pianificati</p>
            <p className="text-xs text-white/70">prima che ci sia un guasto</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        <h2 className="font-display text-2xl font-extrabold text-[#0B1F3A]">Problemi che risolviamo</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Problem icon={AlertTriangle} title="Controlli dimenticati" text="Nessuno tiene traccia di quando è stata l'ultima verifica di un alloggio." />
          <Problem icon={Camera} title="Guasti scoperti dagli ospiti" text="I problemi emergono durante il soggiorno, con recensioni a rischio." />
          <Problem icon={Clock} title="Tempo perso a cercare tecnici" text="Ogni emergenza diventa una caccia al fornitore disponibile." />
          <Problem icon={ClipboardList} title="Assenza di uno storico" text="Mancano report e cronologia degli interventi per ogni immobile." />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        <h2 className="font-display text-2xl font-extrabold text-[#0B1F3A]">Come funziona</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Step n="1" title="Analisi del portafoglio" text="Valutiamo immobili, dotazioni e zona." />
          <Step n="2" title="Proposta" text="La commerciale prepara una proposta dedicata." />
          <Step n="3" title="Inventario e calendario" text="Inventario delle dotazioni e piano delle visite." />
          <Step n="4" title="Visite e report" text="Controlli programmati con report fotografico." />
          <Step n="5" title="Extra autorizzati" text="Interventi extra eseguiti dopo approvazione." />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        <div className="grid gap-4 lg:grid-cols-3">
          {(s?.plans || []).map((p) => (
            <div key={p.id} data-testid={`home-plan-${p.id}`} className="rounded-3xl border border-[#0B1F3A]/10 bg-white p-6 shadow-sm">
              <h3 className="font-display text-xl font-extrabold text-[#0B1F3A]">{p.name}</h3>
              <p className="mt-1 text-sm text-[#0B1F3A]/60">{p.tagline}</p>
              <ul className="mt-4 space-y-2">
                {p.features.slice(0, 3).map((ft, i) => <li key={i} className="flex gap-2 text-sm text-[#0B1F3A]/80"><ChevronRight className="h-4 w-4 shrink-0 text-[#0E7C7B]" />{ft}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <button onClick={() => nav("/piani")} className="rounded-full border border-[#0B1F3A]/15 bg-white px-6 py-3 font-bold text-[#0B1F3A] hover:border-[#0E7C7B]">Vedi tutti i dettagli dei piani</button>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl items-center gap-8 px-5 py-10 lg:grid-cols-2">
        <img src={IMG.filter} alt="Pulizia dei filtri accessibili di uno split" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-lg" />
        <div>
          <h2 className="font-display text-2xl font-extrabold text-[#0B1F3A]">Cosa comprende una visita</h2>
          <ul className="mt-4 space-y-3">
            {[[Search, "Inventario delle dotazioni e controlli visivi non invasivi"], [Wrench, "Pulizia dei filtri accessibili degli split inclusi"], [Camera, "Report fotografico dell'immobile"], [AlertTriangle, "Segnalazione delle anomalie con priorità"], [CalendarCheck, "Calendario del controllo successivo"]].map(([Icon, t], i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#0B1F3A]/80"><Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#0E7C7B]" />{t}</li>
            ))}
          </ul>
          <div className="mt-5 rounded-2xl bg-[#F97316]/10 p-4 text-sm text-[#0B1F3A]/80">
            <p className="font-bold text-[#0B1F3A]">Cosa resta extra</p>
            Riparazioni, manodopera straordinaria e materiali non sono inclusi: vengono gestiti come extra dopo approvazione.
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl items-center gap-8 px-5 py-10 lg:grid-cols-2">
        <div>
          <Building2 className="h-8 w-8 text-[#0E7C7B]" />
          <h2 className="mt-2 font-display text-2xl font-extrabold text-[#0B1F3A]">Gestione di più immobili</h2>
          <p className="mt-2 text-sm text-[#0B1F3A]/70">Inventari separati, calendario delle visite per zona, storico per ogni immobile e un referente dedicato per richieste e preventivi.</p>
          <button onClick={() => nav("/property-manager")} className="mt-5 flex items-center gap-2 text-sm font-bold text-[#0E7C7B]">Scopri l'area property manager <ArrowRight className="h-4 w-4" /></button>
        </div>
        <div className="rounded-3xl border border-[#0B1F3A]/10 bg-white p-6 shadow-sm">
          <MapPin className="h-6 w-6 text-[#F97316]" />
          <h3 className="mt-2 font-display text-lg font-bold text-[#0B1F3A]">Zone servite</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {(s?.zones || []).map((z) => <span key={z} className="rounded-full bg-[#0E7C7B]/10 px-3 py-1 text-xs font-semibold text-[#0E7C7B]">{z}</span>)}
          </div>
          <p className="mt-3 text-xs text-[#0B1F3A]/50">{s?.coverage_note}</p>
        </div>
      </section>

      <FAQSection faq={s?.faq || []} />

      <section className="mx-auto max-w-4xl px-5 py-14">
        <div className="rounded-3xl bg-[#0B1F3A] p-8 text-center text-white sm:p-12">
          <h2 className="font-display text-2xl font-extrabold sm:text-3xl">Richiedi una proposta per i tuoi immobili</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-white/70">Raccontaci portafoglio, dotazioni e zone. La commerciale verifica la copertura e prepara una proposta dedicata.</p>
          <button data-testid="home-cta-bottom" onClick={() => nav("/contatti")} className="mt-6 rounded-full bg-[#F97316] px-8 py-3.5 font-bold text-white shadow-lg transition hover:brightness-110">Richiedi una proposta</button>
        </div>
      </section>
    </B2BLayout>
  );
}

function FAQSection({ faq }) {
  const [open, setOpen] = React.useState(0);
  return (
    <section className="mx-auto max-w-3xl px-5 py-10">
      <h2 className="font-display text-2xl font-extrabold text-[#0B1F3A]">Domande frequenti</h2>
      <div className="mt-5 space-y-2">
        {faq.map((item, i) => (
          <div key={i} className="overflow-hidden rounded-2xl border border-[#0B1F3A]/10 bg-white">
            <button data-testid={`faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-bold text-[#0B1F3A]">
              {item.q}<ChevronRight className={`h-5 w-5 shrink-0 text-[#0E7C7B] transition ${open === i ? "rotate-90" : ""}`} />
            </button>
            {open === i && <p className="px-5 pb-4 text-sm text-[#0B1F3A]/70">{item.a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}
